import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { CarmenUser } from "@carmen/auth-client";
import { signOut } from "@/lib/auth";
import { IconChevronLeft, IconChevronRight, IconClose } from "./Icons";
import { SIDEBAR_GROUPS, type AdminTab, type SidebarItem } from "./sidebarConfig";

const COLLAPSE_KEY = "admin.sidebar.collapsed";

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

function saveCollapsed(v: boolean): void {
  try {
    localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0");
  } catch {
    /* empty */
  }
}

export function AdminSidebar({
  active,
  onSelect,
  visibleTabs,
  user,
  accessRole,
  mobileOpen,
  onMobileClose,
}: {
  active: AdminTab;
  onSelect: (tab: AdminTab) => void;
  visibleTabs: Set<AdminTab>;
  user: CarmenUser | null;
  accessRole: string;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(() => loadCollapsed());

  const toggleCollapse = () => {
    setCollapsed((v) => {
      saveCollapsed(!v);
      return !v;
    });
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Sluit menu"
          onClick={onMobileClose}
          className="fixed inset-0 z-40 bg-[rgba(10,52,96,0.30)] backdrop-blur-sm md:hidden"
        />
      ) : null}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-[rgba(10,52,96,0.10)] bg-white transition-transform duration-200 md:sticky md:top-0 md:h-screen md:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } ${collapsed ? "md:w-[72px]" : "md:w-[240px]"} w-[260px]`}
      >
        <SidebarHeader collapsed={collapsed} onMobileClose={onMobileClose} />

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {SIDEBAR_GROUPS.map((group) => {
            const items = group.items.filter((item) => visibleTabs.has(item.key));
            if (items.length === 0) return null;
            return (
              <div key={group.caption ?? "_"} className="mb-5 last:mb-0">
                {group.caption && !collapsed ? (
                  <p className="cs-caption mb-1.5 px-2.5">{group.caption}</p>
                ) : null}
                {group.caption && collapsed ? (
                  <div
                    aria-hidden
                    className="mx-auto mb-2 h-px w-6 bg-[rgba(10,52,96,0.10)]"
                  />
                ) : null}
                <ul className="space-y-0.5">
                  {items.map((item) => (
                    <SidebarLink
                      key={item.key}
                      item={item}
                      active={active === item.key}
                      collapsed={collapsed}
                      onClick={() => {
                        onSelect(item.key);
                        onMobileClose();
                      }}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="border-t border-[rgba(10,52,96,0.10)] p-3">
          <UserFooter user={user} accessRole={accessRole} collapsed={collapsed} />
        </div>

        <button
          type="button"
          onClick={toggleCollapse}
          aria-label={collapsed ? "Klap menu uit" : "Klap menu in"}
          className="hidden md:flex absolute -right-3 top-20 h-6 w-6 items-center justify-center rounded-full border border-[rgba(10,52,96,0.10)] bg-white text-[rgba(10,52,96,0.55)] shadow-[0_4px_8px_rgba(10,52,96,0.06)] hover:text-[var(--cs-navy)]"
        >
          {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
        </button>
      </aside>
    </>
  );
}

function SidebarHeader({
  collapsed,
  onMobileClose,
}: {
  collapsed: boolean;
  onMobileClose: () => void;
}) {
  return (
    <div className="flex h-[72px] items-center justify-between border-b border-[rgba(10,52,96,0.10)] px-4">
      <Link to="/" className="flex items-center gap-2">
        <img src="/carmen-logo.png" alt="Carmen" className="h-8 w-auto" />
        {!collapsed ? (
          <span className="cs-caption -ml-1 mt-0.5 text-[var(--cs-rose)]">
            Admin
          </span>
        ) : null}
      </Link>
      <button
        type="button"
        onClick={onMobileClose}
        aria-label="Sluit menu"
        className="md:hidden text-[rgba(10,52,96,0.55)] hover:text-[var(--cs-navy)]"
      >
        <IconClose />
      </button>
    </div>
  );
}

function SidebarLink({
  item,
  active,
  collapsed,
  onClick,
}: {
  item: SidebarItem;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        title={collapsed ? item.label : undefined}
        className={`group relative flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors ${
          active
            ? "bg-[rgba(225,29,72,0.06)] text-[var(--cs-navy)]"
            : "text-[rgba(10,52,96,0.62)] hover:bg-[var(--cs-ivory)] hover:text-[var(--cs-navy)]"
        }`}
      >
        {active ? (
          <span
            aria-hidden
            className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-[var(--cs-rose)]"
          />
        ) : null}
        <Icon
          className={`shrink-0 ${active ? "text-[var(--cs-rose)]" : "text-[rgba(10,52,96,0.55)] group-hover:text-[var(--cs-navy)]"}`}
        />
        {!collapsed ? <span className="truncate">{item.label}</span> : null}
      </button>
    </li>
  );
}

function UserFooter({
  user,
  accessRole,
  collapsed,
}: {
  user: CarmenUser | null;
  accessRole: string;
  collapsed: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  if (!user) return null;
  const meta = user.user_metadata as Record<string, unknown> | null;
  const fullName = typeof meta?.full_name === "string" && meta.full_name ? meta.full_name : null;
  const email = user.email ?? "";
  const label = fullName ?? email ?? "Account";
  const initial = (label || "?").charAt(0).toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-[var(--cs-ivory)] ${
          collapsed ? "justify-center" : ""
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgba(225,29,72,0.10)] text-xs font-semibold text-[var(--cs-rose)]">
          {initial}
        </span>
        {!collapsed ? (
          <span className="flex min-w-0 flex-1 flex-col text-left">
            <span className="truncate text-[12px] font-medium text-[var(--cs-navy)]">
              {label}
            </span>
            <span className="cs-caption mt-0.5 normal-case tracking-normal">
              {accessRole.replace(/_/g, " ")}
            </span>
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-50 mb-2 w-52 overflow-hidden rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white py-1 shadow-[0_24px_48px_rgba(10,52,96,0.14)]"
        >
          {!collapsed ? null : (
            <div className="border-b border-[rgba(10,52,96,0.10)] px-3 py-2">
              <p className="truncate text-xs font-medium text-[var(--cs-navy)]">{label}</p>
              <p className="truncate text-[10px] text-[var(--cs-ink-muted)]">{email}</p>
            </div>
          )}
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
          >
            Profiel
          </Link>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="block w-full px-3 py-2 text-left text-xs text-[var(--cs-rose)] hover:bg-[var(--cs-ivory)]"
          >
            Uitloggen
          </button>
        </div>
      ) : null}
    </div>
  );
}
